import { message } from "antd";
import { config } from "../configs";
import { localStrings } from "./constants";
import DM from './DataManagement';
import { sortByTurkishCharacters } from './helperFunctions'

const configURL = config.server_url;
const { fastSearchType } = localStrings;

function getAccessToken() {
    return localStorage.getItem("access_token");
}

function getRefreshToken() {
    return localStorage.getItem("refresh_token");
}

function clearAuthData() {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("token_type");
    localStorage.removeItem("access_token_expires_at");
    localStorage.removeItem("refresh_token_expires_at");
    localStorage.removeItem("username");
    localStorage.removeItem("name");
    localStorage.removeItem("role");
    DM.clearLoggedInUser();
}

function persistAuthData(data) {
    localStorage.setItem("access_token", data.access_token || "");
    localStorage.setItem("refresh_token", data.refresh_token || "");
    localStorage.setItem("token_type", data.tokenType || "Bearer");
    localStorage.setItem("access_token_expires_at", data.accessTokenExpiresAt || "");
    localStorage.setItem("refresh_token_expires_at", data.refreshTokenExpiresAt || "");
    localStorage.setItem("username", data.user?.username || "");
    localStorage.setItem("name", data.user?.name || "");
    localStorage.setItem("role", data.user?.role || "");

    DM.setLoggedInUser({
        username: data.user?.username || "",
        name: data.user?.name || "",
        role: data.user?.role || "",
        access_token: data.access_token || "",
        refresh_token: data.refresh_token || "",
        token_type: data.tokenType || "Bearer",
        access_token_expires_at: data.accessTokenExpiresAt || "",
        refresh_token_expires_at: data.refreshTokenExpiresAt || "",
    });
}

async function refreshAccessToken() {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
        clearAuthData();
        return null;
    }

    try {
        const response = await fetch(`${configURL}/users/refresh`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
            },
            body: JSON.stringify({ refreshToken }),
        });

        const data = await response.json();

        if (!response.ok || !data?.success || !data?.access_token || !data?.refresh_token) {
            clearAuthData();
            return null;
        }

        persistAuthData(data);
        return data.access_token;
    } catch (err) {
        console.log(err);
        clearAuthData();
        return null;
    }
}

export function withAuthHeader(headers = {}) {
  const token = getAccessToken();
  if (!token) return headers;

  return {
    ...headers,
    Authorization: `Bearer ${token}`,
  };
}

export async function requestToServer(endPoint, requestOptions = {}, allowRetry = true) {
    let url = configURL + endPoint;

    const options = {
        ...requestOptions,
        headers: {
            ...(requestOptions.headers || {}),
        },
    };

    let response = await fetch(url, options);
    let obj = await response.json().catch(() => null);

    if (response.status === 401 && allowRetry) {
        const newAccessToken = await refreshAccessToken();

        if (newAccessToken) {
            const retriedOptions = {
                ...options,
                headers: {
                    ...options.headers,
                    Authorization: `Bearer ${newAccessToken}`,
                },
            };

            response = await fetch(url, retriedOptions);
            obj = await response.json().catch(() => null);
        }
    }

    if (!response.ok || !obj?.success) {
        if (response.status === 401 || response.status === 403 || obj?.status === 401 || obj?.status === 403) {
          message.error(obj?.message || "Oturum süresi doldu. Lütfen tekrar giriş yapın.");
          clearAuthData();

          if (typeof window !== "undefined" && window.location.pathname !== "/login") {
            window.location.replace("/login");
          }

          return { success: false, status: response.status, data: null, message: obj?.message };
        }

        if (obj?.message) {
            message.error(obj.message);
        }

        return {
            success: false,
            status: response.status,
            data: obj?.data ?? null,
            message: obj?.message,
        };
    }

    return obj;
}

export async function checkToken(type) {
    let endPoint = "/admin/check-token?type=" + type;
    let requestOptions = {
        method: "GET",
        headers: withAuthHeader(),
    };

    const response = await requestToServer(endPoint, requestOptions);

    // console.log('Token Check response:', response)
    return response;
}

export async function fetchCategories() {
    let url = configURL + "/categories";

    if (DM.getCategories()) {
        return DM.getCategories();
    }

    return fetch(url)
        .then((response) => response.json())
        .then((obj) => {
            console.log('Categories are fetched'); //, obj.data)
            DM.setCategories(obj.data);
            return obj.data;
        });
}

export async function fetchBrands() {
    let url = configURL + "/brands?view=admin-list";

    if (DM.getBrands())
        return DM.getBrands().sort((a, b) => sortByTurkishCharacters(a.brand_name, b.brand_name));

    return fetch(url)
        .then((response) => response.json())
        .then((obj) => {
            console.log('Brands are fetched'); //, obj.data)
            const sorted = obj.data.sort((a, b) => sortByTurkishCharacters(a.brand_name, b.brand_name));
            DM.setBrands(sorted);
            return sorted;
        });
}

export async function fetchBrand(id) {
    let url = configURL + "/brands/" + id;

    const requestOptions = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...withAuthHeader()
        },
        credentials: 'same-origin',
        withCredentials: true
    };

    return fetch(url, requestOptions)
        .then((response) => response.json())
        .then((obj) => {
            console.log('Brand is fetched'); //, obj.data)
            DM.updateBrands(obj.data);
            return obj.data;
        });
}

/**
 * If there exists a query string then request it from db, otherwise return from the Data Management's product pool
 * @param {String} queryStr sample query string is like that: category=L&brand_id=BR-000002&collection_name=MEGA&
 */
export async function fetchProducts(queries) {
    // let url = configURL + '/products/withfilter?' + queryStr; // "/products/withfilter?brand_id=BR-000002";
    let url = configURL + '/products/withfilter'
    
    if (!queries)
        if (DM.getProducts()) {
            // console.log('from.DM:', DM.getProducts())
            return DM.getProducts();
        }

    const requestOptions = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        body: JSON.stringify({ queries }),
        // mode: 'no-cors',
        credentials: 'same-origin',
        withCredentials: true
    };

    return fetch(url, requestOptions)
        .then((response) => response.json())
        .then((obj) => {
            DM.setProducts(obj.data);
            console.log('Products are fetched'); //, obj)
            return obj.data;
        });
}

export async function fetchProduct(id) {
    let url = configURL + "/products/id/" + id; // previously /products/materia_id

    const requestOptions = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        credentials: 'same-origin',
        withCredentials: true
    };

    return fetch(url, requestOptions)
        .then((response) => response.json())
        .then((obj) => {
            // DM.setProduct(obj.data);
            console.log('Product: ' + obj.data.materia_id + ' is fetched'); //, obj.data)
            return obj.data;
        });
}

/**
 * It asynchronously fetches all the information related to given dealer id
 * @param {Object<Dealer>} dealer Dealer object as a parameter
 * @param {number} num
 * @returns {Promise<Dealer>}
 */
export async function fetchDealers() {
    let url = configURL + "/dealers";

    if (DM.getDealers())
        return DM.getDealers();

    const requestOptions = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...withAuthHeader()
        },
        credentials: 'same-origin',
        withCredentials: true
    };

    return fetch(url, requestOptions)
        .then((response) => response.json())
        .then((obj) => {
            DM.setDealers(obj.data);
            console.log('Dealers are fetched'); //, obj.data)
            return obj.data;
        });
}

//! will be deleted later, because we have advanced filter function
export const fetchProductsWithCategory = async (category_id) => {
    let url = configURL + "/categories/" + category_id + "/products";

    return fetch(url)
        .then((response) => response.json())
        .then((obj) => {
            DM.setProducts(obj.data);
            return obj.data;
        })
}

export async function deleteProduct(id) {

    let endPoint = "/products/id/" + id + "/delete";

    const requestOptions = {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...withAuthHeader()
        }
    };
    const response = await requestToServer(endPoint, requestOptions);

    // console.log('dbFunctions::Delete product response:', response)
    return response;
}

export async function deleteBrand(id) {

    let endPoint = "/brands/" + id + "/delete";

    const requestOptions = {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...withAuthHeader()
        }
    };

    const response = await requestToServer(endPoint, requestOptions);

    // console.log('dbFunctions::Delete brand response:', response)
    return response;

}

export async function deleteDealer(id) {

    let endPoint = "/dealers/" + id + "/delete";

    const requestOptions = {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...withAuthHeader()
        }
    };

    const response = await requestToServer(endPoint, requestOptions);

    // console.log('dbFunctions::Delete dealer response:', response)
    return response;

}

export async function fetchUsers() {
    if (DM.getUsers())
        return await DM.getUsers();

    let endPoint = "/users/all?userRole=user";
    let requestOptions = {
        method: "GET",
        headers: withAuthHeader(),
    };

    const response = await requestToServer(endPoint, requestOptions);

    // console.log('dbFunctions::Fetch users response:', response)
    DM.setUsers(response.data);
    return response.data;
}

export async function fetchPersonels() {
    if (DM.getPersonels())
        return await DM.getPersonels();

    let endPoint = "/users/all?userRole=personel";
    let requestOptions = {
        method: "GET",
        headers: withAuthHeader(),
    };

    const response = await requestToServer(endPoint, requestOptions);

    // console.log('dbFunctions::Fetch personels response:', response)
    DM.setPersonels(response.data);
    return response.data;
}

export async function deleteUser(username) {
    let endPoint = "/users/delete/" + username;

    const requestOptions = {
        method: "DELETE",
        headers: {
            ...withAuthHeader()
        }
    };

    const response = await requestToServer(endPoint, requestOptions);
    return response;
}

export async function deleteCategory(id) {
    let endPoint = "/categories/" + id + "/delete";

    const requestOptions = {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...withAuthHeader()
        },
        credentials: 'same-origin',
        withCredentials: true
    };

    const response = await requestToServer(endPoint, requestOptions);
    return response;
}

export async function fetchBlogs() {
    if (DM.getBlogs())
        return DM.getBlogs();

    let endPoint = "/blog/blogs";

    const response = await requestToServer(endPoint);
    DM.setBlogs(response.data);
    return response.data;

}

export async function fetchSlider() {
    if (DM.getSlider())
        return DM.getSlider();

    let endPoint = "/blog/slider";

    const response = await requestToServer(endPoint);
    DM.setSlider(response.data);
    return response.data;
}


export async function fetchBrandsCollectionNames(brand_id) {
    let endPoint = "/brands/" + brand_id + "/collection_names";

    const response = await requestToServer(endPoint);
    // console.log("fetchBrandsCollectionNames.response:", response);
    if (response && response.count > 0) {
        return response.data.sort((a, b) => sortByTurkishCharacters(a, b)).map((name) => {
            return { value: name };
        });
    } else {
        return []
    }
}

export async function fetchBrandsModels(brand_id, collection_name) {
    let endPoint = "/brands/" + brand_id + "/" + collection_name + "/models";
    // console.log("fetchBrandsModels.endPoint:", endPoint);
    const response = await requestToServer(endPoint);
    // console.log("fetchBrandsModels.response:", response);
    if (response && response.count > 0) {
        return response.data.sort((a, b) => sortByTurkishCharacters(a, b)).map((name) => {
            return { value: name };
        });
    } else {
        return []
    }
}

export async function getProductsForAudit(obj) {
    let endPoint = "/admin/product-audit";
    // console.log("getProductsForAudit.endPoint:", endPoint);
    let requestOptions = {
        method: "POST",
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...withAuthHeader()
        },
        body: JSON.stringify(obj)
    };
    const response = await requestToServer(endPoint, requestOptions);
    // console.log("getProductsForAudit.response:", response);
    return response;
}

export async function fetchProfessionals() {
    if (DM.getProfessionals()) {
        return await DM.getProfessionals();
    }

    let endPoint = "/admin/get-all-professionals";
    let requestOptions = {
        method: "GET",
        headers: withAuthHeader()
    };

    const response = await requestToServer(endPoint, requestOptions);
    DM.setProfessionals(response.data)
    return response.data;
}


export async function fetchServices() {
    if (DM.getServices()) {
        return DM.getServices();
    }

    let endPoint = "/services";
    let requestOptions = {
        method: "GET",
        headers: withAuthHeader()
    };

    const response = await requestToServer(endPoint, requestOptions);
    DM.setServices(response.data)
    return response.data;
}

export async function deleteService(id) {
    let endPoint = "/services/" + id + "/delete"

    const requestOptions = {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...withAuthHeader()
        },
        credentials: 'same-origin',
        withCredentials: true
    };

    const response = await requestToServer(endPoint, requestOptions);
    return response;
}

export async function fetchFastSearches(type) {
    let endPoint = "/fast-search?type=";
    let requestOptions = {
        method: "GET",
        headers: withAuthHeader()
    };
    let response;

    if (type === fastSearchType.service) {
        if (DM.getServiceFastSearches()) {
            return DM.getServiceFastSearches();
        }
        endPoint += fastSearchType.service;
        // console.log('endpoint.service', endPoint)
        response = await requestToServer(endPoint, requestOptions);
        // console.log('endpoint.response',response)
        DM.setServiceFastSearches(response.data.data)
        return response.data.data;
    }
    if (type === fastSearchType.product) {
        if (DM.getProductFastSearches()) {
            return DM.getProductFastSearches();
        }
        endPoint += fastSearchType.product;
        // console.log('endpoint.product', endPoint)
        response = await requestToServer(endPoint, requestOptions);
        // console.log('endpoint.response',response)
        DM.setProductFastSearches(response.data.data)
        return response.data.data;
    }
}

export async function fetchHomepageSegments() {
    let url = configURL + "/homepage-segments";

    if (DM.getHomepageSegments()) {
        return DM.getHomepageSegments();
    }

    return fetch(url)
        .then((response) => response.json())
        .then((obj) => {
            console.log('Homepage segments are fetched');
            DM.setHomepageSegments(obj.data);
            return obj.data;
        })
        .catch((error) => {
            console.error('Error fetching homepage segments:', error);
            throw error;
        });
}

export async function fetchHomepageSegment(type) {
    let existSegment = DM.getHomepageSegment(type);
    if (existSegment) {
        return existSegment;
    }

    let url = `${configURL}/segments/${type}`;

    const requestOptions = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...withAuthHeader()
        },
        credentials: 'same-origin',
        withCredentials: true
    };

    return fetch(url, requestOptions)
        .then((response) => response.json())
        .then((obj) => {
            console.log('Homepage segment is fetched');
            DM.setHomepageSegment(obj.data);
            return obj.data;
        })
        .catch((error) => {
            console.error('Error fetching homepage segment:', error);
            throw error;
        });
}

export async function fetchQuickSearchOptions() {
    let url = configURL + "/quicksearch-options";

    if (DM.getQuickSearchOptions()) {
        return DM.getQuickSearchOptions();
    }

    return fetch(url)
        .then((response) => response.json())
        .then((obj) => {
            console.log('Quick search options are fetched');
            DM.setQuickSearchOptions(obj.data);
            return obj.data;
        })
        .catch((error) => {
            console.error('Error fetching quick search options:', error);
            throw error;
        });
}

